import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowSquareOut, Package, PencilSimple, Plus } from 'shared/icons';
import {
    AlertBadges,
    CostBreakdownTable,
    DASH,
    FormatDay,
    FormatFraction,
    IcmsTypePill,
    LotsTable,
    RuleFormModal,
} from '../components/pricing/pricingShared';
import Button from '../components/ui/Button';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { ICMS_TYPE_DESCRIPTIONS, MATCHED_BY_LABELS, RULE_SCOPE_LABELS } from '../constants/pricing';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { FormatDecimal1, FormatInt, FormatMoney } from '../utils/format';
import { MLB_URLS, SecureMlImageUrl } from '../utils/ml';

/**
 * PÁGINA DO SKU (custo fiscal) — tudo o que o sistema sabe sobre o custo de
 * UM produto: cadastro (Bling) e foto (anúncio ML), a regra de ICMS que
 * está valendo (e por quê), os parâmetros fiscais efetivos, o cálculo do
 * custo líquido do último lote e o histórico custo × entrada × NF.
 */

const Kpi = ({ label, value, sub = null, tone = 'text-black dark:text-white' }) => (
    <div className="rounded-xl border border-mono-200 bg-white px-4 py-3 dark:border-mono-800 dark:bg-mono-950">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">{label}</p>
        <p className={`mt-1 truncate text-lg font-bold tabular-nums ${tone}`}>{value}</p>
        {sub ? <p className="mt-0.5 truncate text-xs text-mono-500 dark:text-mono-400">{sub}</p> : null}
    </div>
);

const SectionCard = ({ title, action = null, children }) => (
    <section className="space-y-3 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
        <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-mono-600 dark:text-mono-400">{title}</h2>
            {action}
        </div>
        {children}
    </section>
);

const ParamRow = ({ label, value, overridden = false }) => (
    <div className="flex items-baseline justify-between gap-3 border-b border-mono-100 py-1 text-sm last:border-0 dark:border-mono-900">
        <span className="text-mono-600 dark:text-mono-300">{label}</span>
        <span
            className={`font-mono tabular-nums ${overridden ? 'font-semibold text-info-700 dark:text-info-300' : ''}`}
            title={overridden ? 'sobrescrito pela regra' : ''}
        >
            {value}
            {overridden ? ' *' : ''}
        </span>
    </div>
);

const PricingSkuPage = () => {
    const { sku } = useParams();
    const { checkPermission } = useAuth();
    const canWrite = checkPermission('pricing:write');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);
    const [ruleModal, setRuleModal] = useState({ open: false, initial: null, preset: null });

    const refresh = useCallback(() => setRefreshTick((tick) => tick + 1), []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.getPricingSku(sku)
            .then((payload) => {
                if (!cancelled) {
                    setData(payload || null);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar o SKU.');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [sku, refreshTick]);

    if (loading && !data) {
        return <LoadingSpinner className="py-16" />;
    }
    if (error || !data) {
        return (
            <div className="space-y-4">
                <Link
                    to="/precificacao"
                    className="inline-flex items-center gap-1 text-sm text-info-600 hover:underline dark:text-info-400"
                >
                    <ArrowLeft size={16} /> Precificação
                </Link>
                <div className="rounded-xl border border-danger-200 bg-danger-50 p-4 text-sm text-danger-800 dark:border-danger-900 dark:bg-danger-950 dark:text-danger-200">
                    {error || `SKU ${sku} não encontrado.`}
                </div>
            </div>
        );
    }

    const product = data.product;
    const listing = data.listing;
    const title = product?.title || listing?.title || data.lots?.[0]?.description || data.sku;
    const latestLot = data.lots?.[0] || null;
    const latestCost = latestLot?.cost || null;
    const unit = latestCost?.per_unit || null;
    const rule = data.rule || {};
    const params = data.params || {};
    const effective = params.effective || {};
    const overrides = params.rule_overrides || {};
    const isSt = latestCost?.is_st;

    const openEditRule = () => setRuleModal({ open: true, initial: rule.full, preset: null });
    const openNewRule = () =>
        setRuleModal({
            open: true,
            initial: null,
            preset: {
                name: `${data.sku} — ${title}`.slice(0, 120),
                scope_type: 'sku',
                scope_value: data.sku,
                icms_type: data.icms_type_suggested || rule.icms_type || 'normal',
            },
        });

    return (
        <div className="space-y-5">
            <Link
                to="/precificacao"
                className="inline-flex items-center gap-1 text-sm text-info-600 hover:underline dark:text-info-400"
            >
                <ArrowLeft size={16} /> Precificação
            </Link>

            {/* CABEÇALHO: foto, título, identificação e cadastro */}
            <header className="flex flex-col gap-4 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950 md:flex-row md:items-start">
                <div className="flex h-[120px] w-[120px] shrink-0 items-center justify-center overflow-hidden rounded-lg border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-900">
                    {listing?.thumbnail ? (
                        <img
                            src={SecureMlImageUrl(listing.thumbnail)}
                            alt=""
                            className="h-full w-full object-contain"
                        />
                    ) : (
                        <Package size={36} className="text-mono-300 dark:text-mono-700" />
                    )}
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                    <h1 className="text-xl font-bold text-black dark:text-white md:text-2xl">{title}</h1>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="rounded-md bg-mono-100 px-1.5 py-px font-mono text-xs text-mono-700 dark:bg-mono-900 dark:text-mono-300">
                            SKU {data.sku}
                        </span>
                        <IcmsTypePill type={rule.icms_type} className="uppercase" />
                        <span className="text-xs text-mono-500">
                            {MATCHED_BY_LABELS[rule.matched_by] || rule.matched_by}
                        </span>
                        {listing?.item_id ? (
                            <a
                                href={MLB_URLS.search(listing.item_id)}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 font-mono text-xs text-info-600 hover:underline dark:text-info-400"
                            >
                                {listing.item_id}
                                {listing.listings > 1 ? ` (+${listing.listings - 1})` : ''} <ArrowSquareOut size={12} />
                            </a>
                        ) : null}
                    </div>
                    <p className="text-xs text-mono-500 dark:text-mono-400">
                        {product ? (
                            <>
                                {product.brand ? `Marca ${product.brand} · ` : ''}
                                {product.ean ? `EAN ${product.ean} · ` : ''}
                                {product.supplier_code ? `Cód. fornecedor ${product.supplier_code} · ` : ''}
                                {product.bling_id ? `Bling #${product.bling_id} · ` : ''}
                                custo Bling {FormatMoney(product.cost_price)}
                            </>
                        ) : (
                            'Sem cadastro no Bling — identificado pela NF-e de compra e/ou pelo anúncio.'
                        )}
                        {data.last_ncm ? ` · NCM ${data.last_ncm}` : ''}
                    </p>
                    <AlertBadges alerts={data.alerts} hide={['SEM_CADASTRO']} />
                </div>
            </header>

            {/* KPIs do custo por unidade (último lote) */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
                <Kpi
                    label="Custo bruto / un"
                    value={FormatMoney(unit?.custo_bruto)}
                    sub={
                        latestLot
                            ? `NF nº ${latestLot.nfe_number || DASH} · ${FormatDay(latestLot.emitted_at)}`
                            : 'sem nota'
                    }
                />
                <Kpi
                    label="Créditos / un"
                    value={unit ? `−${FormatMoney((unit.credito_icms || 0) + (unit.credito_pc || 0))}` : DASH}
                    sub={
                        unit
                            ? `ICMS ${FormatMoney(unit.credito_icms)} · PIS/COFINS ${FormatMoney(unit.credito_pc)}`
                            : null
                    }
                    tone="text-success-700 dark:text-success-400"
                />
                {isSt ? <Kpi label="CMV SP / un" value={FormatMoney(unit?.cmv_sp)} sub="venda dentro de SP" /> : null}
                {isSt ? (
                    <Kpi
                        label="CMV fora / un"
                        value={FormatMoney(unit?.cmv_fora)}
                        sub={`recupera ${FormatMoney(latestCost?.recuperacao_esperada_fora)} de ST no lote`}
                    />
                ) : null}
                <Kpi
                    label={isSt ? 'CMV mix / un' : 'CMV líquido / un'}
                    value={FormatMoney(unit?.cmv_blend)}
                    sub={isSt ? `${FormatFraction(latestCost?.params_used?.mix_sp)} SP` : 'sem ST: SP = fora'}
                />
                <Kpi
                    label="Média ponderada / un"
                    value={FormatMoney(data.aggregate?.weighted?.cmv_blend)}
                    sub={
                        data.aggregate?.lots_count
                            ? `${FormatInt(data.aggregate.lots_count)} lote(s) · ${FormatDecimal1(data.aggregate.qty_total)} un`
                            : 'sem lotes'
                    }
                />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {/* REGRA */}
                <SectionCard
                    title="Regra de ICMS aplicada"
                    action={
                        canWrite ? (
                            rule.full ? (
                                <Button size="sm" variant="secondary" onClick={openEditRule}>
                                    <span className="inline-flex items-center gap-1">
                                        <PencilSimple size={14} /> Editar regra
                                    </span>
                                </Button>
                            ) : (
                                <Button size="sm" onClick={openNewRule}>
                                    <span className="inline-flex items-center gap-1">
                                        <Plus size={14} /> Regra para este SKU
                                    </span>
                                </Button>
                            )
                        ) : null
                    }
                >
                    <div className="space-y-2 text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                            <IcmsTypePill type={rule.icms_type} />
                            <span className="text-mono-600 dark:text-mono-300">
                                {rule.full ? (
                                    <>
                                        <strong>{rule.full.name}</strong> · {RULE_SCOPE_LABELS[rule.full.scope_type]}
                                        {rule.full.scope_value ? ` ${rule.full.scope_value}` : ''}
                                        {rule.full.valid_from || rule.full.valid_to
                                            ? ` · ${rule.full.valid_from ? FormatDay(rule.full.valid_from) : 'sempre'} → ${rule.full.valid_to ? FormatDay(rule.full.valid_to) : 'aberta'}`
                                            : ''}
                                    </>
                                ) : (
                                    'Nenhuma regra casa — está valendo o tratamento padrão da empresa.'
                                )}
                            </span>
                        </div>
                        <p className="text-xs text-mono-500">{ICMS_TYPE_DESCRIPTIONS[rule.icms_type]}</p>
                        {data.icms_type_suggested && data.icms_type_suggested !== rule.icms_type ? (
                            <p className="rounded-lg bg-warning-50 p-2 text-xs text-warning-800 dark:bg-warning-900/20 dark:text-warning-200">
                                A última nota (CST/CFOP) sugere <strong>{data.icms_type_suggested}</strong>. Se estiver
                                certo, crie/edite a regra do SKU com esse tipo.
                            </p>
                        ) : null}
                        {rule.full?.note ? <p className="text-xs italic text-mono-500">{rule.full.note}</p> : null}
                        {rule.related?.length ? (
                            <div className="pt-1">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-mono-400">
                                    Regras que alcançam este SKU
                                </p>
                                <ul className="mt-1 space-y-0.5 text-xs">
                                    {rule.related.map((entry) => (
                                        <li
                                            key={entry.id}
                                            className={
                                                entry.id === rule.full?.id
                                                    ? 'font-semibold text-black dark:text-white'
                                                    : 'text-mono-600 dark:text-mono-300'
                                            }
                                        >
                                            {RULE_SCOPE_LABELS[entry.scope_type]}
                                            {entry.scope_value ? ` ${entry.scope_value}` : ''} · {entry.name} ·{' '}
                                            {entry.icms_type}
                                            {!entry.active ? ' · inativa' : ''}
                                            {entry.id === rule.full?.id ? ' ← aplicada' : ''}
                                            {canWrite ? (
                                                <button
                                                    type="button"
                                                    className="ml-2 text-info-600 hover:underline dark:text-info-400"
                                                    onClick={() =>
                                                        setRuleModal({ open: true, initial: entry, preset: null })
                                                    }
                                                >
                                                    editar
                                                </button>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </div>
                </SectionCard>

                {/* PARÂMETROS FISCAIS EFETIVOS */}
                <SectionCard
                    title="Parâmetros fiscais em vigor"
                    action={
                        <Link
                            to="/precificacao?tab=params"
                            className="text-xs text-info-600 hover:underline dark:text-info-400"
                        >
                            editar na aba Parâmetros
                        </Link>
                    }
                >
                    <div>
                        <ParamRow
                            label="PIS/COFINS (crédito na entrada)"
                            value={FormatFraction(effective.pis_cofins_pct)}
                            overridden={'pis_cofins_pct' in overrides}
                        />
                        <ParamRow
                            label="Excluir ICMS da base do crédito"
                            value={effective.credito_pis_cofins_exclui_icms ? 'sim' : 'não'}
                            overridden={'credito_pis_cofins_exclui_icms' in overrides}
                        />
                        <ParamRow
                            label="Alíquota de ICMS da compra (última nota)"
                            value={FormatFraction(latestLot?.aliq_icms_compra)}
                        />
                        <ParamRow
                            label="Honorário de recuperação do ST"
                            value={FormatFraction(effective.honorario_recuperacao)}
                            overridden={'honorario_recuperacao' in overrides}
                        />
                        <ParamRow
                            label="ST estimado (fallback)"
                            value={FormatFraction(effective.st_fallback_pct)}
                            overridden={'st_fallback_pct' in overrides}
                        />
                        <ParamRow
                            label="Mix de vendas em SP"
                            value={FormatFraction(effective.mix_sp)}
                            overridden={'mix_sp' in overrides}
                        />
                        <ParamRow label="Tratamento padrão da empresa" value={effective.default_icms_type || DASH} />
                    </div>
                    {Object.keys(overrides).length ? (
                        <p className="text-[11px] text-mono-500">* sobrescrito pela regra aplicada</p>
                    ) : null}
                </SectionCard>
            </div>

            {/* CÁLCULO DO ÚLTIMO LOTE */}
            <SectionCard
                title={
                    latestLot
                        ? `Cálculo do custo líquido — último lote (NF nº ${latestLot.nfe_number || DASH}, ${latestLot.issuer_name || DASH})`
                        : 'Cálculo do custo líquido'
                }
            >
                {latestCost ? (
                    <>
                        <p className="text-xs text-mono-500">
                            Item {latestLot.item_number} · {latestLot.description || DASH} · CST {latestLot.cst || DASH}{' '}
                            / CFOP {latestLot.cfop || DASH} · {FormatDecimal1(latestLot.qty)} {latestLot.unit || 'un'}
                            {latestLot.icms_substituto
                                ? ` · ICMS substituto ${FormatMoney(latestLot.icms_substituto)}`
                                : ''}
                        </p>
                        <CostBreakdownTable cost={latestCost} />
                        {latestCost.alerts?.length ? <AlertBadges alerts={latestCost.alerts} /> : null}
                    </>
                ) : (
                    <p className="text-sm italic text-mono-500">
                        Nenhuma NF-e de compra vinculada a este SKU. Importe o XML na aba{' '}
                        <Link to="/precificacao?tab=nfe" className="text-info-600 hover:underline dark:text-info-400">
                            Notas de entrada
                        </Link>{' '}
                        e vincule o item.
                    </p>
                )}
            </SectionCard>

            {/* HISTÓRICO */}
            <SectionCard title={`Histórico de custo × entrada × NF (${FormatInt(data.lots?.length || 0)} lote(s))`}>
                <LotsTable
                    lots={data.lots || []}
                    showSku={false}
                    onLotUpdated={refresh}
                    exportFileName={`custo-${data.sku}`}
                />
            </SectionCard>

            <RuleFormModal
                isOpen={ruleModal.open}
                initial={ruleModal.initial}
                preset={ruleModal.preset}
                onClose={() => setRuleModal({ open: false, initial: null, preset: null })}
                onSaved={refresh}
            />
        </div>
    );
};

export default PricingSkuPage;
